import type { ReactNode } from "react";
import { twJoin } from "tailwind-merge";

import type { DesignSystemElementProps, DesignSystemRef } from "@/design-system/contracts/dom";

const ACTIVITY_FEED_DIRECTION_CLASSES = {
  normal: "flex-col",
  reverse: "flex-col-reverse",
} as const;

const ACTIVITY_FEED_PRESENTATION_CLASSES = {
  bordered: "flex gap-md border border-border-default px-space-sm",
  soft: "block bg-background-secondary p-space-sm",
} as const;

export type ActivityFeedProps = {
  children: ReactNode;
  direction?: keyof typeof ACTIVITY_FEED_DIRECTION_CLASSES;
  presentation?: keyof typeof ACTIVITY_FEED_PRESENTATION_CLASSES;
} & DesignSystemElementProps<"div"> &
  DesignSystemRef<HTMLDivElement>;

/** Bordered, vertically scrolling activity flow with an optional reversed visual order. */
export function ActivityFeed({
  children,
  direction = "normal",
  presentation = "bordered",
  ref,
  ...nativeProps
}: ActivityFeedProps): ReactNode {
  return (
    <div
      {...nativeProps}
      ref={ref}
      className={twJoin(
        "max-h-scroll-area-standard overflow-y-auto rounded-lg",
        presentation === "bordered" && ACTIVITY_FEED_DIRECTION_CLASSES[direction],
        ACTIVITY_FEED_PRESENTATION_CLASSES[presentation],
      )}
    >
      {children}
    </div>
  );
}

/** A wrapping notice and action row shown above an activity feed. */
export function ActivityFeedPrompt({ message, actions }: { message: ReactNode; actions: ReactNode }): ReactNode {
  return (
    <aside className="flex w-full flex-wrap items-center gap-space-sm py-space-md">
      <div className="flex flex-1">{message}</div>
      {actions}
    </aside>
  );
}

/** Centered activity-feed footer for pagination actions and loading states. */
export function ActivityFeedFooter({
  children,
  isLoading = false,
  ref,
  ...nativeProps
}: {
  children: ReactNode;
  isLoading?: boolean;
} & Omit<DesignSystemElementProps<"div">, "children"> &
  DesignSystemRef<HTMLDivElement>): ReactNode {
  return (
    <div
      {...nativeProps}
      ref={ref}
      className={twJoin(
        "flex w-full items-center justify-center",
        isLoading ? "px-space-md py-space-xl" : "p-space-md",
      )}
    >
      {children}
    </div>
  );
}
